import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const formUrl = 'https://readdy.ai/api/form/d9pbpgaqs38sj6jc7lf0';

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于韩国漫画网 - 品牌介绍与联系方式',
      description: '韩国漫画网是中国领先的免费韩漫在线阅读平台，致力于为中文读者提供最优质的韩国漫画阅读体验。',
      url: 'https://www.koreancomic.cn/about',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '韩国漫画网需要注册吗？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '不需要。韩国漫画网所有漫画均可免费在线阅读，无需注册或登录，打开网站即可畅享高清韩漫。',
          },
        },
        {
          '@type': 'Question',
          name: '韩国漫画网的漫画多久更新一次？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '我们每日更新，确保读者能够第一时间阅读到最新的韩漫章节。热门作品通常每周更新1-2次。',
          },
        },
        {
          '@type': 'Question',
          name: '如何在韩国漫画网查找想看的漫画？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '您可以通过首页的分类导航浏览不同题材的漫画，也可以使用顶部搜索栏直接搜索漫画名称。我们还提供热门推荐和最新更新列表，帮助您发现精彩的韩漫作品。',
          },
        },
        {
          '@type': 'Question',
          name: '韩国漫画网支持手机阅读吗？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '是的，韩国漫画网采用响应式设计，完美适配手机、平板和电脑等各类设备，让您随时随地畅享韩漫阅读。',
          },
        },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 韩国漫画网 | 品牌介绍与联系方式"
        description="韩国漫画网是中国领先的免费韩漫在线阅读平台，致力于为中文读者提供最优质的韩国漫画阅读体验。了解我们的品牌故事、用户口碑与联系方式。"
        keywords="韩国漫画网,关于我们,韩漫网站,联系我们,漫画平台"
        canonicalUrl="https://www.koreancomic.cn/about"
        structuredData={structuredData}
      />

      <Navbar />

      <main className="pt-24 pb-10 md:pb-14">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <section className="mb-14">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                关于韩国漫画网
              </h1>
              <p className="text-foreground-600 leading-relaxed text-base">
                韩国漫画网成立于2024年，是中国领先的免费韩漫在线阅读平台。我们致力于为中文读者打造最全面、最优质的韩国漫画阅读体验，让每一位漫画爱好者都能轻松畅享韩漫的魅力。
              </p>
            </section>

            <section className="mb-14">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
                我们的故事
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-background-100 rounded-xl p-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-xl mb-4">
                    <i className="ri-lightbulb-line text-primary-600 text-xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                    品牌起源
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    韩国漫画网的创始团队是一群热爱韩国漫画的资深读者。我们发现中文互联网上缺少一个专门为韩漫爱好者打造的优质阅读平台，于是决定亲手创建一个。从最初的几十部漫画到现在数千部作品，我们始终保持着对韩漫的热爱与初心。
                  </p>
                </div>
                <div className="bg-background-100 rounded-xl p-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-accent-100 rounded-xl mb-4">
                    <i className="ri-team-line text-accent-600 text-xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                    团队愿景
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们希望成为中文世界最值得信赖的韩漫阅读平台。不仅提供海量优质的漫画资源，更致力于营造一个健康、积极的漫画爱好者社区。让更多人认识并爱上韩国漫画的独特魅力。
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  {
                    name: '小雨',
                    avatar: 'https://readdy.ai/api/search-image?query=A%20cute%20Korean%20style%20cartoon%20girl%20avatar%20portrait%2C%20soft%20pastel%20colors%2C%20simple%20clean%20background%2C%20anime%20style%20illustration&width=80&height=80&seq=avatar-01&orientation=squarish',
                    role: '忠实读者',
                    comment: '这个网站太棒了！画质清晰、更新及时，而且完全免费。我每天都会上来看看有没有新更新的韩漫，已经成了我的日常习惯。',
                    rating: 5,
                  },
                  {
                    name: '星辰大海',
                    avatar: 'https://readdy.ai/api/search-image?query=A%20cool%20Korean%20style%20cartoon%20boy%20avatar%20portrait%2C%20modern%20clean%20style%2C%20simple%20background%2C%20anime%20style%20illustration&width=80&height=80&seq=avatar-02&orientation=squarish',
                    role: '漫画达人',
                    comment: '分类做得非常清晰，找漫画特别方便。校园和奇幻类是我最喜欢的，推荐的作品质量都很高。希望网站越来越好！',
                    rating: 5,
                  },
                  {
                    name: '樱花酱',
                    avatar: 'https://readdy.ai/api/search-image?query=A%20sweet%20Korean%20style%20cartoon%20girl%20avatar%20with%20pink%20hair%2C%20soft%20colors%2C%20simple%20clean%20background%2C%20anime%20style%20illustration&width=80&height=80&seq=avatar-03&orientation=squarish',
                    role: '恋爱漫画迷',
                    comment: '这里的恋爱韩漫资源太全了！很多在别的地方找不到的作品这里都有。强烈推荐给喜欢看韩国恋爱漫画的姐妹们。',
                    rating: 5,
                  },
                ].map((review) => (
                  <div key={review.name} className="bg-background-100 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                        <img
                          src={review.avatar}
                          alt={review.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                        <p className="text-xs text-foreground-500">{review.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                      "{review.comment}"
                    </p>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
                常见问题
              </h2>
              <div className="space-y-3">
                {[
                  {
                    q: '韩国漫画网需要注册吗？',
                    a: '不需要。韩国漫画网所有漫画均可免费在线阅读，无需注册或登录，打开网站即可畅享高清韩漫。',
                  },
                  {
                    q: '韩国漫画网的漫画多久更新一次？',
                    a: '我们每日更新，确保读者能够第一时间阅读到最新的韩漫章节。热门作品通常每周更新1-2次。',
                  },
                  {
                    q: '如何在韩国漫画网查找想看的漫画？',
                    a: '您可以通过首页的分类导航浏览不同题材的漫画，也可以使用顶部搜索栏直接搜索漫画名称。我们还提供热门推荐和最新更新列表，帮助您发现精彩的韩漫作品。',
                  },
                  {
                    q: '韩国漫画网支持手机阅读吗？',
                    a: '是的，韩国漫画网采用响应式设计，完美适配手机、平板和电脑等各类设备，让您随时随地畅享韩漫阅读。',
                  },
                ].map((faq, i) => (
                  <details key={i} className="bg-background-100 rounded-lg overflow-hidden group">
                    <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-200/50 transition-colors">
                      <h3 className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</h3>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-600">contact@koreancomic.cn</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                      <i className="ri-global-line text-accent-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">官方网站</h3>
                      <p className="text-sm text-foreground-600">www.koreancomic.cn</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-secondary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">更新时间</h3>
                      <p className="text-sm text-foreground-600">每日 00:00 更新最新漫画章节</p>
                    </div>
                  </div>
                </div>

                <form
                  action={formUrl}
                  method="POST"
                  className="bg-background-100 rounded-xl p-6"
                  data-readdy-form=""
                  id="contact-form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                    if (honeypot) {
                      const successMsg = document.getElementById('contact-success');
                      if (successMsg) successMsg.classList.remove('hidden');
                      return;
                    }
                    const formData = new FormData(form);
                    try {
                      const res = await fetch(form.action, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                      });
                      const text = await res.text();
                      let parsed;
                      try { parsed = JSON.parse(text); } catch { parsed = null; }
                      if (res.ok && parsed?.code === 'OK') {
                        const successMsg = document.getElementById('contact-success');
                        if (successMsg) successMsg.classList.remove('hidden');
                        const errMsg = document.getElementById('contact-error');
                        if (errMsg) errMsg.classList.add('hidden');
                        form.reset();
                      } else {
                        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || '提交失败';
                        const errEl = document.getElementById('contact-error');
                        if (errEl) {
                          errEl.textContent = serverMsg;
                          errEl.classList.remove('hidden');
                        }
                      }
                    } catch {
                      const errEl = document.getElementById('contact-error');
                      if (errEl) {
                        errEl.textContent = '网络错误，请稍后再试';
                        errEl.classList.remove('hidden');
                      }
                    }
                  }}
                >
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">发送消息</h3>
                  <div className="space-y-3">
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                      aria-hidden="true"
                      className="anti-spam-field"
                    />
                    <textarea
                      name="message"
                      placeholder="请输入您的留言内容（最多500字）"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    ></textarea>
                    <button
                      type="submit"
                      className="w-full bg-primary-500 text-background-50 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      发送消息
                    </button>
                    <p id="contact-success" className="text-sm text-accent-600 hidden">
                      消息已发送成功！我们会尽快回复您。
                    </p>
                    <p id="contact-error" className="text-sm text-red-500 hidden"></p>
                  </div>
                </form>
              </div>
            </section>

            <section className="mt-14 pt-10 border-t border-background-200/70 text-center">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                开启你的韩漫之旅
              </h2>
              <p className="text-foreground-500 mb-6">
                数千部优质韩漫等你来发现，完全免费，无需注册
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </section>
          </div>
        </div>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}